import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import '../landing/tokens.css';
import c from './Presentation.module.css';
import {
  CLUSTERS,
  HUE,
  MAP,
  NUMBERS,
  PROJECT_COUNT,
  RISKS,
  THESES,
  slideTitle,
  slides,
  type ProjectSlide,
  type Slide,
} from './deck';

/* Hidden investor deck. Reached only by typing /presentation — nothing on the site links here.
   Keys: ← → / space / PageUp / PageDown / Home / End step; O or Escape toggles the overview grid. */

const hueOf = (s: Slide) => (s.kind === 'project' ? s.hue : HUE.platform);

export function Presentation() {
  const [i, setI] = useState(() => {
    const n = Number(window.location.hash.replace('#', ''));
    return Number.isFinite(n) && n >= 1 && n <= slides.length ? n - 1 : 0;
  });
  const [overview, setOverview] = useState(false);

  const go = useCallback((next: number) => {
    setI(Math.max(0, Math.min(slides.length - 1, next)));
  }, []);

  useEffect(() => {
    window.history.replaceState(null, '', `#${i + 1}`);
  }, [i]);

  // Deep links: /presentation#12 jumps to slide 12, and editing the hash moves the deck.
  useEffect(() => {
    const onHash = () => {
      const n = Number(window.location.hash.replace('#', ''));
      if (Number.isFinite(n) && n >= 1 && n <= slides.length) setI(n - 1);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Keep the deck out of search results without naming the path in robots.txt.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'eXonstech — Portfolio';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      meta.remove();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          setOverview(false);
          setI((v) => Math.min(slides.length - 1, v + 1));
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          setOverview(false);
          setI((v) => Math.max(0, v - 1));
          break;
        case 'Home':
          setI(0);
          break;
        case 'End':
          setI(slides.length - 1);
          break;
        case 'o':
        case 'O':
          setOverview((v) => !v);
          break;
        case 'Escape':
          setOverview((v) => !v);
          break;
        default:
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const slide = slides[i];
  const hue = hueOf(slide);
  const style = {
    ['--slide-hue' as any]: hue,
    ['--slide-glow' as any]: `color-mix(in srgb, ${hue} 9%, transparent)`,
  } as CSSProperties;

  return (
    <div className={c.deck} style={style}>
      <div className={c.stage}>
        <div className={c.glow} />
        <div className={c.grid} />
        <div className={c.slide} key={i}>
          <SlideBody slide={slide} />
        </div>
      </div>

      <div className={c.footer}>
        <div className={c.brand}>
          <span className={c.brandDot} />
          eXonstech · portfolio · confidential
        </div>
        <span className={c.hint}>← → step · O overview</span>
        <div className={c.nav}>
          <button className={c.navBtn} onClick={() => setOverview((v) => !v)} title="Overview (O)">
            ▦
          </button>
          <button className={c.navBtn} onClick={() => go(i - 1)} disabled={i === 0} title="Previous">
            ←
          </button>
          <span className={c.counter}>
            {String(i + 1).padStart(2, '0')} / {slides.length}
          </span>
          <button
            className={c.navBtn}
            onClick={() => go(i + 1)}
            disabled={i === slides.length - 1}
            title="Next"
          >
            →
          </button>
        </div>
      </div>

      <div className={c.bar}>
        <div className={c.barFill} style={{ width: `${((i + 1) / slides.length) * 100}%` }} />
      </div>

      {overview && (
        <div className={c.overview}>
          <div className={c.overviewHead}>
            <h2 className={c.h2} style={{ margin: 0 }}>
              Overview
            </h2>
            <button className={c.navBtn} onClick={() => setOverview(false)}>
              close
            </button>
          </div>
          <div className={c.overviewGrid}>
            {slides.map((s, n) => (
              <button
                key={n}
                className={`${c.thumb} ${n === i ? c.thumbActive : ''}`}
                style={{ ['--thumb-hue' as any]: hueOf(s) } as CSSProperties}
                onClick={() => {
                  go(n);
                  setOverview(false);
                }}
              >
                <span className={c.thumbNum}>{String(n + 1).padStart(2, '0')}</span>
                <span className={c.thumbTitle}>{slideTitle(s)}</span>
                {s.kind === 'project' && <span className={c.thumbSub}>{s.category}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SlideBody({ slide }: { slide: Slide }) {
  switch (slide.kind) {
    case 'cover':
      return <Cover />;
    case 'map':
      return <MapSlide />;
    case 'theses':
      return <ThesesSlide />;
    case 'clusters':
      return <ClustersSlide />;
    case 'numbers':
      return <NumbersSlide />;
    case 'risks':
      return <RisksSlide />;
    case 'close':
      return <CloseSlide />;
    default:
      return <ProjectBody p={slide} />;
  }
}

function Cover() {
  return (
    <div className={c.cover}>
      <div className={c.coverMark}>Portfolio review · August 2026 · confidential</div>
      <h1 className={c.coverTitle}>
        We build the <em>hard half</em> of applied AI.
      </h1>
      <p className={c.lead}>
        Nineteen platforms across infrastructure, regulated verticals and generative media — built on
        custom agent runtimes, evaluated retrieval, sovereign inference and an Unreal Engine
        digital-human stack. Every number in this deck is traceable to a working tree.
      </p>
      <div className={c.coverStats}>
        {[
          ['19', 'platforms'],
          ['5', 'live or in production'],
          ['1', 'shipped game'],
          ['0', 'agent frameworks used'],
        ].map(([v, l]) => (
          <div className={c.coverStat} key={l}>
            <b>{v}</b>
            <span>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MapSlide() {
  return (
    <div>
      <p className={c.eyebrow}>01 · Portfolio</p>
      <h2 className={c.h2}>What exists, and how far along it is</h2>
      <p className={c.lead}>
        Grouped by evidence, not ambition. Stage claims come from the repositories themselves —
        tests, CI, deploy manifests and what each README says is <em>not</em> built.
      </p>
      <div className={c.bands}>
        {MAP.map((band) => (
          <div className={c.band} key={band.band}>
            <div className={c.bandHead}>
              <span className={c.bandTitle}>{band.band}</span>
              <span className={c.bandNote}>{band.note}</span>
            </div>
            <div className={c.bandItems}>
              {band.items.map((it) => (
                <div
                  className={c.bandItem}
                  key={it.name}
                  style={{ ['--item-hue' as any]: it.hue } as CSSProperties}
                >
                  <b>{it.name}</b>
                  <span>{it.what}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThesesSlide() {
  return (
    <div>
      <p className={c.eyebrow}>02 · Thesis</p>
      <h2 className={c.h2}>Three things every project in here believes</h2>
      <p className={c.lead}>
        Not a strategy imposed on the portfolio afterwards — these are patterns the code already
        shares, in trees built years and markets apart.
      </p>
      <div className={c.theses}>
        {THESES.map((t) => (
          <div className={c.thesis} key={t.n}>
            <div className={c.thesisNum}>{t.n}</div>
            <h3>{t.title}</h3>
            <p>{t.body}</p>
            <p className={c.thesisWhy}>{t.why}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClustersSlide() {
  return (
    <div>
      <p className={c.eyebrow}>03 · Capability</p>
      <h2 className={c.h2}>What we demonstrably do</h2>
      <div className={c.clusters}>
        {CLUSTERS.map((cl) => (
          <div className={c.cluster} key={cl.n}>
            <div className={c.clusterHead}>
              <span className={c.clusterNum}>{cl.n}</span>
              <h3>{cl.title}</h3>
            </div>
            <p>{cl.body}</p>
            <p className={c.clusterProof}>{cl.proof}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectBody({ p }: { p: ProjectSlide }) {
  return (
    <div>
      <p className={c.eyebrow}>
        Project {String(p.index).padStart(2, '0')} of {PROJECT_COUNT}
        {p.hook && <span className={c.hook}>{p.hook}</span>}
      </p>
      <div className={c.projHead}>
        <span className={c.projNum}>{String(p.index).padStart(2, '0')}</span>
        <div>
          <h2 className={c.projName}>{p.name}</h2>
          <div className={c.projTags}>
            <span className={`${c.tag} ${c.tagHue}`}>{p.category}</span>
            <span className={c.tag}>{p.stage}</span>
            {p.live && <span className={`${c.tag} ${c.tagLive}`}>{p.live}</span>}
          </div>
        </div>
      </div>

      <div className={c.oneLiner}>
        <p className={c.oneLinerText}>{p.oneLiner}</p>
        {p.hookNote && <p className={c.hookNote}>{p.hookNote}</p>}
      </div>

      <div className={c.cols}>
        <div>
          <p className={c.blockLabel}>The pitch</p>
          <p className={c.pitch}>{p.pitch}</p>

          <p className={c.blockLabel}>Proof</p>
          <div className={c.metrics}>
            {p.metrics.map((m) => (
              <div className={c.metric} key={m.label}>
                <b>{m.value}</b>
                <span>{m.label}</span>
              </div>
            ))}
          </div>

          <div className={c.stack}>
            {p.stack.map((s) => (
              <span className={c.chip} key={s}>
                {s}
              </span>
            ))}
          </div>
        </div>

        <div>
          <p className={c.blockLabel}>What it does</p>
          <ul className={c.features}>
            {p.features.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>

          <div className={c.straight}>
            <p>
              <strong style={{ color: 'var(--fg-2)' }}>Straight talk — </strong>
              {p.straight}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function NumbersSlide() {
  return (
    <div>
      <p className={c.eyebrow}>Evidence</p>
      <h2 className={c.h2}>Numbers we can defend under diligence</h2>
      <p className={c.lead}>
        Each of these is produced by a test suite, a benchmark script or a release gate in a working
        tree — not by a slide.
      </p>
      <div className={c.numberGrid}>
        {NUMBERS.map((n) => (
          <div className={c.numberCell} key={n.label}>
            <b>{n.value}</b>
            <span>{n.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RisksSlide() {
  return (
    <div>
      <p className={c.eyebrow}>Diligence</p>
      <h2 className={c.h2}>The questions we would ask, asked first</h2>
      <p className={c.lead}>
        Every project in this deck has a “straight talk” line for a reason. Here are the portfolio-level
        ones.
      </p>
      <div className={c.risks}>
        {RISKS.map((r) => (
          <div className={c.risk} key={r.risk}>
            <div>
              <div className={c.riskName}>{r.risk}</div>
              <span className={c.riskWhere}>{r.where}</span>
            </div>
            <p className={c.riskAnswer}>{r.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CloseSlide() {
  const asks = [
    {
      h: 'Focus the portfolio',
      p: 'Nineteen platforms is a research asset, not a company. The next step is picking the two or three that go to market first — the current front-runners are ZakhuSign, Playback and Capsule — and putting the rest behind them.',
    },
    {
      h: 'Turn alphas into products',
      p: 'The gap between our best trees and revenue is operational, not architectural: CI on every repo, secret rotation, deployment, and the first trained domain models for Playback.',
    },
    {
      h: 'Buy compute and hands',
      p: 'Everything GPU-bound today runs on one RTX 4090. The generative-media and vision lines are written to scale out and have never been given a fleet — or a second pair of hands.',
    },
    {
      h: 'Land the design partners',
      p: 'Regulated buyers — brokerages, law firms, clubs — are the proof our sovereignty and audit architecture is worth what it cost. LensEvents shows we can run in production when someone is paying.',
    },
  ];
  return (
    <div>
      <p className={c.eyebrow}>Close</p>
      <h2 className={c.h2}>What the next twelve months buy</h2>
      <div className={c.asks}>
        {asks.map((a) => (
          <div className={c.ask} key={a.h}>
            <h3>{a.h}</h3>
            <p>{a.p}</p>
          </div>
        ))}
      </div>
      <div className={c.closeContact}>
        eXonstech · exonstech.com · full technical inventory: docs/portfolio-tech-inventory.md
      </div>
    </div>
  );
}

export default Presentation;
